import axios from 'axios';

const STAGING_SESSION_API_BASE = 'https://staging.id.sonio-group.com/api/v4';

export type SessionApiEnvironment = 'dev' | 'staging';

export type SessionTheme = 'light' | 'dark' | '';

/** Novibet POA — always staging, even when configExists is true. */
export const NOVIBET_POA_FLOW_ID = '6a7eb1af1c5ef1f03c31767f';

/** Flow IDs that always use staging for create-session (regardless of configExists). */
export const STAGING_SESSION_FLOW_IDS = new Set([
  NOVIBET_POA_FLOW_ID,
  '6a7edf82c6400b6c73c4ab25',
]);

/**
 * Session API environment for Flow Management create-session.
 * Always staging.id (dev.id is no longer used for session creation).
 */
export function getSessionEnvironmentForFlow(_flow?: {
  id: string;
  configExists?: boolean;
}): SessionApiEnvironment {
  return 'staging';
}

export function getSessionApiBase(
  _env: SessionApiEnvironment = 'staging',
): string {
  return STAGING_SESSION_API_BASE;
}

export interface SessionGenerationFormData {
  referenceId: string;
  redirectUrl: string;
  firstname: string;
  lastname: string;
  maidenname: string;
  gender: string;
  birthdate: string;
  addressLine1: string;
  postcode: string;
  city: string;
  country: string;
  /** COV — first 6 digits of the card (BIN). */
  cardFirstDigits: string;
  /** COV — last 4 digits of the card. */
  cardLastDigits: string;
  theme?: SessionTheme;
  language: string;
}

export interface CreateSonioSessionOptions {
  customerId: string;
  flowId: string;
  language?: string;
  form: SessionGenerationFormData;
  /** Defaults to staging.id. */
  environment?: SessionApiEnvironment;
  theme?: SessionTheme;
}

export interface CreateSonioSessionResponse {
  startURL?: string;
  sessionId?: string;
  [key: string]: unknown;
}

export function buildSessionPayload({
  customerId,
  flowId,
  language = 'en',
  form,
  theme,
}: CreateSonioSessionOptions) {
  const sessionTheme = (theme ?? form.theme ?? '').trim();
  const sessionLanguage = form.language?.trim() || language || 'en';

  const cardFirstDigits = form.cardFirstDigits?.trim() || '';
  const cardLastDigits = form.cardLastDigits?.trim() || '';

  return {
    customerId,
    language: sessionLanguage,
    flowId,
    ...(sessionTheme ? { theme: sessionTheme } : {}),
    redirectURL: form.redirectUrl || 'https://www.google.com',
    ...(form.referenceId.trim()
      ? { referenceId: form.referenceId.trim() }
      : {}),
    person: {
      firstname: form.firstname,
      lastname: form.lastname,
      maidenname: form.maidenname,
      gender: form.gender,
      birthdate: form.birthdate,
    },
    address: {
      addressLine1: form.addressLine1,
      postcode: form.postcode,
      city: form.city,
      country: form.country,
    },
    ...(cardFirstDigits || cardLastDigits
      ? {
          card: {
            ...(cardFirstDigits ? { firstDigits: cardFirstDigits } : {}),
            ...(cardLastDigits ? { lastDigits: cardLastDigits } : {}),
          },
        }
      : {}),
  };
}

export async function createSonioSession(
  options: CreateSonioSessionOptions,
): Promise<CreateSonioSessionResponse> {
  const payload = buildSessionPayload(options);
  const base = getSessionApiBase(options.environment ?? 'staging');
  const { data } = await axios.post<CreateSonioSessionResponse>(
    `${base}/session`,
    payload,
    { headers: { 'Content-Type': 'application/json' } },
  );
  return data;
}

export function openSessionStartUrl(data: CreateSonioSessionResponse): boolean {
  const startURL = data?.startURL;
  if (typeof startURL === 'string' && startURL) {
    window.open(startURL, '_blank');
    return true;
  }
  return false;
}
